/**
 * Selection Store
 *
 * Zustand store for managing workspace item selection state.
 * Supports single and multi-select with keyboard modifiers.
 *
 * Features:
 * - Individual item selection/deselection
 * - Multi-select mode with Cmd/Ctrl+click
 * - Range selection with Shift+click
 * - Select all / clear selection
 * - Automatic mode detection
 * - Action tracking for external sync (e.g., preview updates)
 *
 * @module features/workspace/stores/useSelectionStore
 */

import { create } from 'zustand';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Selection action types for tracking the last operation performed.
 * Used for synchronizing selection changes with external components (e.g., preview panels).
 */
export type SelectionAction = 'select' | 'toggle' | 'range' | 'clear' | 'selectAll';

/**
 * Selection store state
 */
export interface SelectionState {
  /** Set of selected item IDs */
  selectedIds: Set<string>;
  /** Last clicked item ID (anchor point for range selection) */
  lastSelectedId: string | null;
  /** Current selection mode (auto-detected based on selection count) */
  selectionMode: 'single' | 'multi';
  /** Last action performed (for external sync/notifications) */
  lastAction: SelectionAction | null;
}

/**
 * Selection store actions
 */
interface SelectionActions {
  /** Select a single item (replaces current selection) */
  selectItem: (id: string) => void;
  /** Deselect a single item */
  deselectItem: (id: string) => void;
  /** Toggle item selection (for Cmd/Ctrl+click) */
  toggleSelect: (id: string) => void;
  /** Select all items from a list */
  selectAll: (ids: string[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Select a range of items (for Shift+click) */
  selectRange: (targetId: string, allItemIds: string[]) => void;
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Get count of selected items */
  getSelectionCount: () => number;
}

/**
 * Combined store type
 */
export type SelectionStore = SelectionState & SelectionActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: SelectionState = {
  selectedIds: new Set<string>(),
  lastSelectedId: null,
  selectionMode: 'single',
  lastAction: null,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines the selection mode based on the number of selected items.
 * Single mode: 0-1 items selected
 * Multi mode: 2+ items selected
 */
function getSelectionMode(selectedCount: number): 'single' | 'multi' {
  return selectedCount > 1 ? 'multi' : 'single';
}

/**
 * Calculates the range of items between two indices (inclusive).
 * Returns an array of item IDs within the range.
 */
function getItemsInRange(
  allItemIds: string[],
  startIndex: number,
  endIndex: number
): string[] {
  const start = Math.min(startIndex, endIndex);
  const end = Math.max(startIndex, endIndex);
  return allItemIds.slice(start, end + 1);
}

/**
 * Finds the last item ID from an array, or null if empty.
 */
function getLastItemId(ids: string[]): string | null {
  return ids.length > 0 ? ids[ids.length - 1] : null;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Selection store
 *
 * Manages item selection state for the workspace file explorer.
 * Automatically switches between single and multi-select modes.
 * Tracks actions for external synchronization (e.g., preview panels).
 */
export const useSelectionStore = create<SelectionStore>((set, get) => ({
  ...initialState,

  // ==========================================================================
  // SELECTION ACTIONS
  // ==========================================================================

  /**
   * Select a single item, clearing any previous selection.
   * This is the default behavior for a normal click (no modifiers).
   *
   * @param id - Item ID to select
   */
  selectItem: (id) => {
    set({
      selectedIds: new Set([id]),
      lastSelectedId: id,
      selectionMode: 'single',
      lastAction: 'select',
    });
  },

  /**
   * Deselect a single item from the current selection.
   * Useful for programmatic deselection or custom interactions.
   *
   * @param id - Item ID to deselect
   */
  deselectItem: (id) => {
    const newSelected = new Set(get().selectedIds);
    newSelected.delete(id);

    set({
      selectedIds: newSelected,
      selectionMode: getSelectionMode(newSelected.size),
      lastAction: 'toggle',
      // Note: lastSelectedId is preserved to maintain range selection anchor
    });
  },

  /**
   * Toggle item selection (add if not selected, remove if selected).
   * Used for Cmd/Ctrl+click multi-select behavior.
   *
   * @param id - Item ID to toggle
   */
  toggleSelect: (id) => {
    const { selectedIds } = get();
    const newSelected = new Set(selectedIds);

    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }

    set({
      selectedIds: newSelected,
      lastSelectedId: id, // Update anchor for future range selections
      selectionMode: getSelectionMode(newSelected.size),
      lastAction: 'toggle',
    });
  },

  /**
   * Select all items from the provided list.
   * Typically used for "Select All" commands (Cmd/Ctrl+A).
   *
   * @param ids - Array of all item IDs to select
   */
  selectAll: (ids) => {
    set({
      selectedIds: new Set(ids),
      lastSelectedId: getLastItemId(ids), // Set anchor to last item
      selectionMode: getSelectionMode(ids.length),
      lastAction: 'selectAll',
    });
  },

  /**
   * Clear all selections and reset to initial state.
   * Used when clicking empty space or explicitly clearing selection.
   */
  clearSelection: () => {
    set({
      selectedIds: new Set<string>(),
      lastSelectedId: null,
      selectionMode: 'single',
      lastAction: 'clear',
    });
  },

  /**
   * Select a range of items between the last selected item and the target.
   * Used for Shift+click behavior to select multiple contiguous items.
   *
   * Algorithm:
   * 1. If no anchor point (lastSelectedId), select only the target
   * 2. Find indices of anchor and target in the item list
   * 3. Select all items between them (inclusive)
   * 4. Preserve existing selections outside the range (additive behavior)
   *
   * @param targetId - ID of the item to select up to
   * @param allItemIds - Ordered array of all item IDs (defines range order)
   */
  selectRange: (targetId, allItemIds) => {
    const { lastSelectedId, selectedIds } = get();

    // No anchor point - treat as single select
    if (!lastSelectedId) {
      set({
        selectedIds: new Set([targetId]),
        lastSelectedId: targetId,
        selectionMode: 'single',
        lastAction: 'range',
      });
      return;
    }

    // Find positions in the item list
    const lastIndex = allItemIds.indexOf(lastSelectedId);
    const targetIndex = allItemIds.indexOf(targetId);

    // One or both items not found in list - fallback to single select
    if (lastIndex === -1 || targetIndex === -1) {
      set({
        selectedIds: new Set([targetId]),
        lastSelectedId: targetId,
        selectionMode: 'single',
        lastAction: 'range',
      });
      return;
    }

    // Calculate range and add to existing selection
    const rangeItems = getItemsInRange(allItemIds, lastIndex, targetIndex);
    const newSelected = new Set(selectedIds);
    rangeItems.forEach((id) => newSelected.add(id));

    set({
      selectedIds: newSelected,
      lastSelectedId: targetId, // Update anchor to target for next range selection
      selectionMode: getSelectionMode(newSelected.size),
      lastAction: 'range',
    });
  },

  // ==========================================================================
  // QUERY METHODS
  // ==========================================================================

  /**
   * Check if a specific item is currently selected.
   *
   * @param id - Item ID to check
   * @returns true if item is selected, false otherwise
   */
  isSelected: (id) => {
    return get().selectedIds.has(id);
  },

  /**
   * Get the total number of selected items.
   *
   * @returns Count of selected items
   */
  getSelectionCount: () => {
    return get().selectedIds.size;
  },
}));
